import {
  OreHeading,
  OreInput,
  OreSelect,
  OreSelectPlaceholder,
  OreStack,
  OreText,
} from '@edenredespana/oreneta'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useBankAccount } from '@/Contract/ui/context'
import { contractTranslation } from '@/Contract/ui/contractTranslation'
import { BankAccountChangeForm } from '@/Contract/ui/hooks'
import { SITE_INFO } from '@/Shared/constants/siteInfo'
import { IBAN_MAX_LENGTH, documentType, iban } from '@/Shared/domain'
import { handleErrors } from '@/Shared/ui/form'
import { formTranslation } from '@/Shared/ui/sharedTranslation'
import { useUserData } from '@/User/ui/context'

export const Step1Form = (): JSX.Element => {
  const { t } = useTranslation()
  const { availablePhones, currentIban } = useBankAccount()
  const { merchant } = useUserData()
  const { register, formState } = useFormContext<BankAccountChangeForm>()
  const { onChange, ...rest } = register('newIban')
  const { errors } = handleErrors(formState)

  return (
    <OreStack placeContent="stretch" space="large">
      <OreHeading as="h2" size="headline-md">
        {t(contractTranslation.changeBankAccount.form.title)}
      </OreHeading>
      <OreStack direction="row" space="large" placeContent="stretch" sameSize>
        <OreInput
          id="currentIban"
          name="currentIban"
          label={t(contractTranslation.changeBankAccount.form.data.currentIban.label)}
          maxLength={IBAN_MAX_LENGTH}
          disabled
          value={iban(currentIban)}
        />
        <OreInput
          {...rest}
          {...errors('newIban')}
          id="newIban"
          label={t(contractTranslation.changeBankAccount.form.data.newIban.label)}
          placeholder={t(
            contractTranslation.changeBankAccount.form.data.newIban.placeholder,
          )}
          required
          maxLength={IBAN_MAX_LENGTH}
          onChange={e => {
            e.target.value = iban(e.target.value)
            onChange(e)
          }}
        />
      </OreStack>

      <OreHeading as="h2" size="headline-md">
        {t(contractTranslation.changeBankAccount.form.title2)}
      </OreHeading>

      <OreStack direction="row" space="large" placeContent="stretch" sameSize>
        <OreSelect
          {...register('signerPhoneId')}
          {...errors('signerPhoneId')}
          id="signerPhoneId"
          label={t(contractTranslation.changeBankAccount.form.data.signerPhoneId.label)}
          required
          legend={
            !merchant.isNursery
              ? t(contractTranslation.changeBankAccount.form.data.signerPhoneId.legend, {
                  CONTACT_EMAIL: SITE_INFO.contact.email,
                })
              : t(
                  contractTranslation.changeBankAccount.form.data.signerPhoneId
                    .legendGuarderia,
                )
          }
          labelTooltip={t(
            contractTranslation.changeBankAccount.form.data.signerPhoneId.tooltip,
          )}
        >
          <OreSelectPlaceholder
            label={t(
              contractTranslation.changeBankAccount.form.data.signerPhoneId.placeholder,
            )}
          />
          {availablePhones
            ? availablePhones.map(
                phone =>
                  phone.id && (
                    <option key={phone.id} value={phone.id}>
                      {phone.maskedPhoneNumber}
                    </option>
                  ),
              )
            : null}
        </OreSelect>
        <OreInput
          {...register('signerName')}
          {...errors('signerName')}
          id="signerName"
          label={t(contractTranslation.changeBankAccount.form.data.signerName.label)}
          placeholder={t(
            contractTranslation.changeBankAccount.form.data.signerName.placeholder,
          )}
          required
          maxLength={255}
        />
      </OreStack>

      <OreStack direction="row" space="large" placeContent="stretch" sameSize>
        <OreInput
          {...register('signerSurname1')}
          {...errors('signerSurname1')}
          id="signerSurname1"
          label={t(contractTranslation.changeBankAccount.form.data.signerSurname1.label)}
          placeholder={t(
            contractTranslation.changeBankAccount.form.data.signerSurname1.placeholder,
          )}
          required
          maxLength={255}
        />
        <OreInput
          {...register('signerSurname2')}
          {...errors('signerSurname2')}
          id="signerSurname2"
          label={t(contractTranslation.changeBankAccount.form.data.signerSurname2.label)}
          placeholder={t(
            contractTranslation.changeBankAccount.form.data.signerSurname2.placeholder,
          )}
          maxLength={255}
        />
      </OreStack>

      <OreStack direction="row" space="large" placeContent="stretch" sameSize>
        <OreSelect
          {...register('signerDocumentType')}
          {...errors('signerDocumentType')}
          id="signerDocumentType"
          label={t(
            contractTranslation.changeBankAccount.form.data.signerDocumentType.label,
          )}
          legend={t(
            contractTranslation.changeBankAccount.form.data.signerDocumentType.legend,
          )}
          required
        >
          <OreSelectPlaceholder
            label={t(
              contractTranslation.changeBankAccount.form.data.signerDocumentType
                .placeholder,
            )}
          />
          <option value={documentType.DniNie}>
            {t(formTranslation.common.identifier.documentType.dni)}
          </option>
          <option value={documentType.Passport}>
            {t(formTranslation.common.identifier.documentType.passport)}
          </option>
        </OreSelect>
        <OreInput
          {...register('signerDocument')}
          {...errors('signerDocument')}
          id="signerDocument"
          label={t(contractTranslation.changeBankAccount.form.data.signerDocument.label)}
          placeholder={t(
            contractTranslation.changeBankAccount.form.data.signerDocument.placeholder,
          )}
          required
        />
      </OreStack>
      <OreText>{t(formTranslation.common.requiredFields)}</OreText>
    </OreStack>
  )
}
